/**
 * Inventory filters (FS-029).
 *
 * Two layers, deliberately. The top one is what a dealer uses every morning —
 * search, sort, the statuses their own lifecycle actually has, an age band, a
 * couple of one-click work queues. The second is the nested AND/OR builder for
 * the question nobody anticipated.
 *
 * **The hardcoded status list is gone.** It held thirteen names from one
 * dealership's lifecycle; a tenant who configured their own saw filters that
 * matched nothing and lost the ones that would have. Statuses now come from
 * `GET /statuses?entity_type=asset`, grouped by phase and coloured by the
 * status's own colour.
 */

import { useState } from "react";
import {
  Filter,
  X,
  ChevronDown,
  ChevronUp,
  SortAsc,
  Sparkles,
} from "lucide-react";
import CategorySelect from "@/components/ui/CategorySelect";
import Button from "@/components/ui/Button";
import FilterGroupBuilder from "@/features/inventory/components/FilterGroupBuilder";
import { useFilterReference, useStatuses } from "@/features/inventory/api";
import {
  AGE_BANDS,
  EMPTY_FILTERS,
  QUICK_FLAGS,
  countActive,
  isEmptyNode,
  type InventoryFilterState,
} from "@/features/inventory/filterTree";

/** Sorts worth offering by name. The server accepts more; these are the ones people ask for. */
const SORT_OPTIONS = [
  { value: "created_at:desc", label: "Newest first" },
  { value: "created_at:asc", label: "Oldest first" },
  { value: "days_in_inventory:desc", label: "Longest in inventory" },
  { value: "days_in_current_status:desc", label: "Longest in current status" },
  { value: "name:asc", label: "Name A→Z" },
  { value: "purchase_price:desc", label: "Price: high → low" },
  { value: "purchase_price:asc", label: "Price: low → high" },
  { value: "landed_cost:desc", label: "Landed cost: high → low" },
  { value: "year:desc", label: "Year: newest" },
  { value: "status:asc", label: "Status A→Z" },
];

const PHASE_LABELS: Record<string, string> = {
  intake: "Intake",
  reconditioning: "Reconditioning",
  selling: "Selling",
  closed: "Closed",
};

interface InventoryFiltersProps {
  filters: InventoryFilterState;
  onChange: (filters: InventoryFilterState) => void;
  totalResults: number;
}

function Chip({
  active,
  onClick,
  children,
  color,
  title,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
  color?: string;
  title?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      title={title}
      style={
        active && color
          ? { borderColor: color, color, backgroundColor: `${color}1a` }
          : undefined
      }
      className={`inline-flex items-center gap-1.5 rounded-[var(--radius-full)] border px-2.5 py-1 text-xs font-medium transition-colors ${
        active
          ? "border-brand-300 bg-brand-100 text-brand-700 dark:border-brand-700 dark:bg-brand-900/50 dark:text-brand-300"
          : "border-[var(--border-default)] bg-[var(--surface-base)] text-[var(--text-secondary)] hover:bg-[var(--color-surface-hover)]"
      }`}
    >
      {color && (
        <span
          className="h-1.5 w-1.5 shrink-0 rounded-full"
          style={{ backgroundColor: color }}
          aria-hidden="true"
        />
      )}
      {children}
    </button>
  );
}

function Section({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-[var(--text-tertiary)]">
        {label}
      </p>
      {children}
    </div>
  );
}

function InventoryFilters({
  filters,
  onChange,
  totalResults,
}: InventoryFiltersProps) {
  const [expanded, setExpanded] = useState(false);
  const [showBuilder, setShowBuilder] = useState(false);

  const { data: statuses } = useStatuses("asset");
  const { data: reference } = useFilterReference();

  const update = (partial: Partial<InventoryFilterState>) =>
    onChange({ ...filters, ...partial });

  const toggle = (
    key: "statuses" | "ageBands" | "quickFlags",
    value: string,
  ) => {
    const current = filters[key];
    update({
      [key]: current.includes(value)
        ? current.filter((item) => item !== value)
        : [...current, value],
    } as Partial<InventoryFilterState>);
  };

  const activeCount = countActive(filters, filters.advanced);
  const clearAll = () =>
    onChange({
      ...EMPTY_FILTERS,
      search: filters.search,
      sortBy: filters.sortBy,
      sortDir: filters.sortDir,
    });

  const sortValue = `${filters.sortBy}:${filters.sortDir}`;

  // Statuses grouped by the phase the organization gave them. A tenant with no
  // phases set gets one unlabelled group rather than an empty panel.
  const byPhase = new Map<string, typeof statuses>();
  for (const status of statuses ?? []) {
    const phase = status.phase ?? "";
    byPhase.set(phase, [...(byPhase.get(phase) ?? []), status]);
  }

  const conditionIssues = reference?.condition?.condition_issue ?? [];
  const operationalStatuses = reference?.condition?.operational_status ?? [];

  return (
    <div className="space-y-3">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <input
            type="text"
            value={filters.search}
            onChange={(e) => update({ search: e.target.value })}
            placeholder="Search name, SKU, serial, VIN, make, model, lot, vendor, location…"
            className="h-[38px] w-full rounded-[var(--radius-md)] border-[0.5px] border-[var(--border-default)] bg-[var(--surface-base)] pl-3 pr-8 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] focus:outline-none focus-visible:border-[var(--border-focus)] focus-visible:shadow-[var(--focus-ring)]"
          />
          {filters.search && (
            <button
              onClick={() => update({ search: "" })}
              aria-label="Clear search"
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[var(--text-tertiary)] hover:text-[var(--text-primary)]"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>

        <div className="flex items-center gap-2">
          <SortAsc className="h-3.5 w-3.5 text-[var(--text-tertiary)]" />
          <select
            value={sortValue}
            onChange={(e) => {
              const [by, dir] = e.target.value.split(":");
              update({
                sortBy: by || "created_at",
                sortDir: (dir as "asc" | "desc") || "desc",
              });
            }}
            aria-label="Sort"
            className="h-[34px] rounded-[var(--radius-md)] border border-[var(--border-default)] bg-[var(--surface-base)] px-2 text-xs text-[var(--text-primary)]"
          >
            {SORT_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        <Button
          variant="secondary"
          size="sm"
          onClick={() => setExpanded(!expanded)}
        >
          <Filter className="h-3.5 w-3.5" />
          Filters
          {activeCount > 0 && (
            <span className="ml-1 flex h-4 w-4 items-center justify-center rounded-full bg-[var(--color-interactive-primary)] text-[9px] font-bold text-white">
              {activeCount}
            </span>
          )}
          {expanded ? (
            <ChevronUp className="h-3 w-3" />
          ) : (
            <ChevronDown className="h-3 w-3" />
          )}
        </Button>
      </div>

      {/* The work queues. Always visible, because they are the reason somebody
          opened this screen before their first coffee. */}
      <div className="flex flex-wrap items-center gap-1.5">
        {QUICK_FLAGS.map((flag) => (
          <Chip
            key={flag.key}
            active={filters.quickFlags.includes(flag.key)}
            onClick={() => toggle("quickFlags", flag.key)}
          >
            {flag.label}
          </Chip>
        ))}
        <span
          className="mx-1 h-4 w-px bg-[var(--border-subtle)]"
          aria-hidden="true"
        />
        {AGE_BANDS.map((band) => (
          <Chip
            key={band.key}
            active={filters.ageBands.includes(band.key)}
            onClick={() => toggle("ageBands", band.key)}
            title="Days in inventory, measured from the purchase date"
          >
            {band.label}
          </Chip>
        ))}
      </div>

      <div className="flex items-center gap-2 text-xs text-[var(--text-tertiary)]">
        <span>
          {totalResults} asset{totalResults !== 1 ? "s" : ""}
        </span>
        {activeCount > 0 && (
          <>
            <span>
              ({activeCount} filter{activeCount !== 1 ? "s" : ""} active)
            </span>
            <button
              onClick={clearAll}
              className="text-brand-600 hover:underline"
            >
              Clear
            </button>
          </>
        )}
      </div>

      {expanded && (
        <div className="animate-fade-in space-y-4 rounded-[var(--radius-lg)] border border-[var(--border-subtle)] bg-[var(--surface-base)] p-4">
          <Section label="Status">
            {statuses?.length ? (
              <div className="space-y-2">
                {[...byPhase.entries()].map(([phase, group]) => (
                  <div
                    key={phase}
                    className="flex flex-wrap items-center gap-1.5"
                  >
                    {phase && (
                      <span className="w-24 shrink-0 text-[11px] text-[var(--text-tertiary)]">
                        {PHASE_LABELS[phase] ?? phase}
                      </span>
                    )}
                    {(group ?? []).map((status) => (
                      <Chip
                        key={status.id}
                        color={status.color}
                        active={filters.statuses.includes(status.name)}
                        onClick={() => toggle("statuses", status.name)}
                      >
                        {status.name}
                      </Chip>
                    ))}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-[var(--text-tertiary)]">
                No statuses configured yet.
              </p>
            )}
          </Section>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Section label="Category">
              <CategorySelect
                value={filters.category}
                onChange={(v) => update({ category: v })}
              />
            </Section>

            <Section label="Make / model">
              <div className="flex items-center gap-1.5">
                <input
                  value={filters.make}
                  onChange={(e) => update({ make: e.target.value })}
                  placeholder="Make"
                  title="Fuzzy — a near-enough spelling still matches"
                  className="h-8 w-full rounded-[var(--radius-sm)] border border-[var(--border-default)] bg-[var(--surface-base)] px-2 text-xs"
                />
                <input
                  value={filters.model}
                  onChange={(e) => update({ model: e.target.value })}
                  placeholder="Model"
                  className="h-8 w-full rounded-[var(--radius-sm)] border border-[var(--border-default)] bg-[var(--surface-base)] px-2 text-xs"
                />
              </div>
            </Section>

            <Section label="Year">
              <div className="flex items-center gap-1.5">
                <input
                  type="number"
                  value={filters.yearFrom}
                  onChange={(e) => update({ yearFrom: e.target.value })}
                  placeholder="From"
                  className="h-8 w-full rounded-[var(--radius-sm)] border border-[var(--border-default)] bg-[var(--surface-base)] px-2 text-xs"
                />
                <span className="text-xs text-[var(--text-tertiary)]">–</span>
                <input
                  type="number"
                  value={filters.yearTo}
                  onChange={(e) => update({ yearTo: e.target.value })}
                  placeholder="To"
                  className="h-8 w-full rounded-[var(--radius-sm)] border border-[var(--border-default)] bg-[var(--surface-base)] px-2 text-xs"
                />
              </div>
            </Section>

            <Section label="Purchase price">
              <div className="flex items-center gap-1.5">
                <input
                  type="number"
                  value={filters.minPrice}
                  onChange={(e) => update({ minPrice: e.target.value })}
                  placeholder="Min"
                  className="h-8 w-full rounded-[var(--radius-sm)] border border-[var(--border-default)] bg-[var(--surface-base)] px-2 text-xs"
                />
                <span className="text-xs text-[var(--text-tertiary)]">–</span>
                <input
                  type="number"
                  value={filters.maxPrice}
                  onChange={(e) => update({ maxPrice: e.target.value })}
                  placeholder="Max"
                  className="h-8 w-full rounded-[var(--radius-sm)] border border-[var(--border-default)] bg-[var(--surface-base)] px-2 text-xs"
                />
              </div>
            </Section>

            <Section label="Added between">
              <div className="flex items-center gap-1.5">
                <input
                  type="date"
                  value={filters.dateFrom}
                  onChange={(e) => update({ dateFrom: e.target.value })}
                  aria-label="Added from"
                  className="h-8 w-full rounded-[var(--radius-sm)] border border-[var(--border-default)] bg-[var(--surface-base)] px-2 text-xs"
                />
                <span className="text-xs text-[var(--text-tertiary)]">–</span>
                <input
                  type="date"
                  value={filters.dateTo}
                  onChange={(e) => update({ dateTo: e.target.value })}
                  aria-label="Added to"
                  className="h-8 w-full rounded-[var(--radius-sm)] border border-[var(--border-default)] bg-[var(--surface-base)] px-2 text-xs"
                />
              </div>
            </Section>

            <Section label="Runs?">
              <select
                value={filters.operationalStatus}
                onChange={(e) => update({ operationalStatus: e.target.value })}
                aria-label="Operational status"
                className="h-8 w-full rounded-[var(--radius-sm)] border border-[var(--border-default)] bg-[var(--surface-base)] px-2 text-xs"
              >
                <option value="">Any</option>
                {operationalStatuses.map((value) => (
                  <option key={value} value={value}>
                    {value.replace(/_/g, " ")}
                  </option>
                ))}
              </select>
            </Section>

            <Section label="Condition issue">
              <select
                value={filters.conditionIssue}
                onChange={(e) => update({ conditionIssue: e.target.value })}
                aria-label="Condition issue"
                className="h-8 w-full rounded-[var(--radius-sm)] border border-[var(--border-default)] bg-[var(--surface-base)] px-2 text-xs"
              >
                <option value="">Any</option>
                {conditionIssues.map((value) => (
                  <option key={value} value={value}>
                    {value.replace(/_/g, " ")}
                  </option>
                ))}
              </select>
            </Section>

            <Section label="Days in current status">
              <input
                type="number"
                value={filters.daysInStatusOver}
                onChange={(e) => update({ daysInStatusOver: e.target.value })}
                placeholder="At least…"
                aria-label="Days in current status at least"
                className="h-8 w-full rounded-[var(--radius-sm)] border border-[var(--border-default)] bg-[var(--surface-base)] px-2 text-xs"
              />
            </Section>
          </div>

          <div className="border-t border-[var(--border-subtle)] pt-3">
            {showBuilder || filters.advanced ? (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-[var(--text-tertiary)]">
                    Advanced
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      update({ advanced: null });
                      setShowBuilder(false);
                    }}
                    className="text-xs text-[var(--text-tertiary)] hover:text-[var(--text-secondary)]"
                  >
                    Remove
                  </button>
                </div>
                <FilterGroupBuilder
                  node={filters.advanced ?? { all: [] }}
                  reference={reference}
                  onChange={(node) =>
                    update({ advanced: isEmptyNode(node) ? node : node })
                  }
                />
                <p className="text-[11px] text-[var(--text-tertiary)]">
                  Combined with everything above using “and”.
                </p>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setShowBuilder(true)}
                className="inline-flex items-center gap-1.5 text-xs font-medium text-brand-600 hover:text-brand-700"
              >
                <Sparkles className="h-3.5 w-3.5" />
                Build a more specific filter
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default InventoryFilters;
